import React, { useState, useEffect, useRef, memo } from "react";
import GradientCircleAnimation from "./HeroBanner/GradientCircles";

// Project data moved outside of component to prevent re-creation on each render
const projectData = [
  {
    id: 1,
    name: "OYO",
    tagline: "Mobile App Delivery Strategy",
    websiteUrl: "https://www.oyorooms.com/",
    logo: "/clients/oyologo.png",
    coverImage: "/clients/oyocover.jpg",
    shortDescription: "Delivering a seamless and scalable hotel booking experience through the OYO mobile app.",
    vision: "Enhance mobile engagement to increase downloads, retention, and direct bookings while providing a frictionless user journey.",
    challenges: [
      "Increase app downloads",
      "Improve user retention",
      "Reduce dependency on third-party booking platforms"
    ],
    strategy: [
      {
        title: "Frictionless Booking",
        description: "Optimized the app flow for seamless hotel search, booking, and check-in experiences."
      },
      {
        title: "Push Personalization",
        description: "Implemented smart notifications to boost user retention and engagement."
      },
      {
        title: "Loyalty Integration",
        description: "Introduced in-app loyalty program to increase repeat bookings."
      }
    ],
    impacts: [
      "40% Increase in App Installs YoY",
      "60% Bookings via Mobile App",
      "25% Retention Improvement",
      "15% Increase in Repeat Bookings"
    ],
    conclusion: "OYO's mobile-first strategy transformed user acquisition and retention by creating an intuitive and rewarding app experience."
  },
  {
    id: 2,
    name: "Goibibo",
    tagline: "Travel Super App",
    websiteUrl: "https://www.goibibo.com/",
    logo: "/clients/goibibo.png",
    coverImage: "/clients/goibibo.png",
    shortDescription: "An integrated travel booking platform unifying transport and lodging services in one app.",
    vision: "Centralize bookings across flights, trains, buses, and hotels while enhancing real-time updates and user retention.",
    challenges: [
      "Fragmented travel services",
      "User retention and loyalty",
      "Real-time updates for bookings"
    ],
    strategy: [
      {
        title: "Unified Booking Interface",
        description: "Merged multiple services into a seamless booking experience."
      },
      {
        title: "GoCash & Loyalty Gamification",
        description: "Encouraged repeat use through incentives and competitive engagement."
      },
      {
        title: "PNR-Based Push Alerts",
        description: "Enabled personalized and timely updates for train users."
      }
    ],
    impacts: [
      "52% Flight Bookings Shifted to App",
      "30% Repeat Users via GoCash",
      "20% Uplift in Train Bookings"
    ],
    conclusion: "Goibibo's transformation into a travel super app helped retain users by integrating services and adding gamified incentives."
  },
  {
    id: 3,
    name: "EaseMyTrip",
    tagline: "Low-Cost OTA App",
    websiteUrl: "https://www.easemytrip.com/",
    logo: "/clients/ease.png",
    coverImage: "/clients/ease.png",
    shortDescription: "Delivering cost-effective travel booking without hidden charges or downtime.",
    vision: "Offer a no-convenience-fee experience with high reliability and automated post-booking support.",
    challenges: [
      "Price-sensitive customer base",
      "Need for automated support",
      "Zero downtime requirement"
    ],
    strategy: [
      {
        title: "Zero Convenience Fee",
        description: "Built trust with transparent pricing and no hidden charges."
      },
      {
        title: "In-App Fare Alerts",
        description: "Used push notifications to drive price conversions."
      },
      {
        title: "Support Automation",
        description: "Integrated smart help desk to reduce support ticket load."
      }
    ],
    impacts: [
      "40% App Traffic from Price-Sensitive Flyers",
      "2x Conversion via Fare-Drop Notifications",
      "18% Fewer Support Tickets"
    ],
    conclusion: "EaseMyTrip enhanced affordability and support efficiency with a transparent and smart mobile app experience."
  },
  {
    id: 4,
    name: "Parimatch",
    tagline: "Sports Betting App",
    websiteUrl: "https://www.parimatch.com/",
    logo: "/clients/pari.png",
    coverImage: "/clients/pari.png",
    shortDescription: "A fast, legal, and user-centric betting platform optimized for major sports events.",
    vision: "Deliver a secure and feature-rich betting experience optimized for events and accessibility.",
    challenges: [
      "Compliance with local laws",
      "Real-time betting engagement",
      "Night-time usage comfort"
    ],
    strategy: [
      {
        title: "Instant Bets",
        description: "Enabled bet placements in under 3 taps."
      },
      {
        title: "Geo-Targeted UI",
        description: "Modified interface based on legal jurisdictions."
      },
      {
        title: "Live Features & Dark Mode",
        description: "Included streaming and dark mode for extended use."
      }
    ],
    impacts: [
      "70%+ Users Active During Major Events",
      "3x Engagement During IPL",
      "40% Less Churn"
    ],
    conclusion: "Parimatch focused on event-driven features, compliance, and real-time experience to boost engagement and retention."
  },
  {
    id: 5,
    name: "1xBet",
    tagline: "Global Betting Platform",
    websiteUrl: "https://1xbet.com/",
    logo: "/clients/bet.png",
    coverImage: "/clients/bet.png",
    shortDescription: "A multilingual, localized, and feature-rich mobile betting experience for global audiences.",
    vision: "Make betting accessible worldwide through fast, localized, and multi-language mobile solutions.",
    challenges: [
      "Multilingual user base",
      "Local payment adaptation",
      "Mobile feature parity with web"
    ],
    strategy: [
      {
        title: "Global Localization",
        description: "Enabled 60+ language support and currency conversion."
      },
      {
        title: "In-App Game Variety",
        description: "Optimized casino, slots, and dealer games for mobile."
      },
      {
        title: "Smart Segmentation",
        description: "Delivered targeted notifications based on sports interests."
      }
    ],
    impacts: [
      "80% Signups via Mobile",
      "15% More Deposits Post Localization",
      "22% Longer Sessions for Casino Users"
    ],
    conclusion: "1xBet became a global mobile-first platform by combining local adaptation with a wide gaming selection."
  },
  {
    id: 6,
    name: "BetMGM",
    tagline: "US Sports Betting App",
    websiteUrl: "https://sports.betmgm.com/",
    logo: "/clients/betmgm.png",
    coverImage: "/clients/betmgm.png",
    shortDescription: "A U.S.-focused legal sports betting platform integrating promotions and rewards.",
    vision: "Deliver a legally compliant and feature-rich app for U.S. sports fans across states.",
    challenges: [
      "State-by-state legal compliance",
      "User verification security",
      "Fan engagement around events"
    ],
    strategy: [
      {
        title: "Geofencing & Compliance",
        description: "Adapted app per state laws with auto-detection."
      },
      {
        title: "Biometric Login & Verification",
        description: "Secured user access with modern auth flows."
      },
      {
        title: "Immersive Promotions",
        description: "Leveraged AR/VR previews and exclusive rewards."
      }
    ],
    impacts: [
      "50% Revenue Growth in NY/NJ",
      "35% More Installs During March Madness",
      "90% Bonus Claims via App"
    ],
    conclusion: "BetMGM succeeded by mixing legal tech, engaging UX, and targeted promos tailored for U.S. users."
  },
  {
    id: 7,
    name: "Binance",
    tagline: "Crypto Exchange Super App",
    websiteUrl: "https://www.binance.com/",
    logo: "/clients/binance.png",
    coverImage: "/clients/binance.png",
    shortDescription: "A mobile-first crypto platform offering trading, education, and earning features with top-tier security.",
    vision: "Make crypto accessible and secure globally via a unified mobile-first experience.",
    challenges: [
      "Onboarding global users",
      "Need for education within app",
      "Security of transactions and wallets"
    ],
    strategy: [
      {
        title: "All-in-One Crypto Suite",
        description: "Integrated trading, staking, and portfolio tracking in-app."
      },
      {
        title: "Binance Academy & Earn",
        description: "Educated and rewarded users with interactive modules."
      },
      {
        title: "Advanced Mobile Security",
        description: "Used 2FA, face unlock, and device whitelisting for safety."
      }
    ],
    impacts: [
      "70M+ Active Users",
      "85% on Mobile",
      "40% More Engagement via Learning Modules",
      "60% P2P Growth via Mobile Offers"
    ],
    conclusion: "Binance combined education, security, and earning in a single app to dominate mobile crypto usage globally."
  }
];

// Memoized ProjectCard component to prevent unnecessary re-renders
const ProjectCard = memo(({ project, openProjectModal }) => {
  return (
    <div
      className="bg-gray-800/60 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700 hover:border-blue-500 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl hover:shadow-blue-500/20 group"
    >
      {/* <div className="h-48 bg-gray-700 relative overflow-hidden flex items-center justify-center"> */}
        {/* <div className="absolute inset-0 bg-gradient-to-br from-white to-white group-hover:opacity-70 transition-opacity"></div> */}
        {/* Logo Container with standardized dimensions */}
        {/* <div className="relative z-10 flex items-center justify-center"> */}
          {/* <img 
            src={project.logo} 
            alt={`${project.name} logo`}
            className="w-40 h-20 object-fill"
            loading="lazy"
          /> */}
        {/* </div> */}
      {/* </div> */}
      <div className="h-48 bg-gray-200 transition-colors ease-in-out hover:bg-gray-50 relative overflow-hidden flex items-center justify-center">
      <img 
            src={project.logo} 
            alt={`${project.name} logo`}
            className=" object-contain w-64"
            loading="lazy"
          /> 
      </div>
      <div className="p-6">
        <h4 className="text-xl font-semibold text-blue-300 mb-2">{project.tagline}</h4>
        <p className="text-gray-300 mb-6 font-kanit">{project.shortDescription}</p>
        <button
          onClick={() => openProjectModal(project)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors duration-300 flex items-center group-hover:bg-blue-500"
        >
          Explore Case Study
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
});

// Memoized Modal component
const ProjectModal = memo(({ project, modalRef, closeModal }) => {
  return (
    <div 
      ref={modalRef}
      className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-auto relative"
    >
      {/* Close button */}
      <button
        onClick={closeModal}
        className="absolute top-4 right-4 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors duration-300 z-50"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Modal Header */}
      <div className="p-8 pb-0">
        <h2 className="text-4xl font-bold text-white mb-2">{project.name}</h2>
        <h3 className="text-2xl text-blue-400 font-medium mb-4">{project.tagline}</h3>
      </div>

      {/* Cover Image Preview - Fixed aspect ratio container */}
      <div className="flex justify-center items-center my-10 relative">
        <div
          className="relative w-[70%] md:w-[60%] lg:w-[40%] aspect-video shadow-2xl shadow-blue-400 ring-8 rounded-lg overflow-hidden"
          style={{
            transform:
              "perspective(542px) rotate(22deg) rotateX(8.5deg) rotateY(-28deg) skewX(0.6deg) skewY(-16.3deg)",
            transformOrigin: "100% 50% 0px",
            translate: "-3em",
          }}
        >
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            <img
              src={project.coverImage}
              alt={project.name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* Case Study Content */}
      <div className="text-white px-8 pb-10">
        <h2 className="text-3xl font-bold mb-6">Project Vision</h2>

        {/* Vision Section */}
        <div className="mb-8">
          <p className="text-gray-300 font-light font-kanit">
            {project.vision}
          </p>
        </div>

        {/* Challenges Section */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3">The Challenge</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300 font-kanit font-light">
            {project.challenges.map((challenge, index) => (
              <li key={index}>{challenge}</li>
            ))}
          </ul>
        </div>

        {/* Strategy Section */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3">
            Our Strategy for Success
          </h4>

          <ol className="list-decimal list-inside space-y-2 font-kanit font-light text-gray-300">
            {project.strategy.map((item, index) => (
              <li key={index}>
                <b>{item.title}:</b> {item.description}
              </li>
            ))}
          </ol>
        </div>

        {/* Impact Section */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3">The Impact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-light font-kanit">
            {project.impacts.map((impact, index) => (
              <div key={index} className="bg-gray-800/50 p-4 rounded-lg">
                <p><b>{impact}</b></p>
              </div>
            ))}
          </div>
        </div>

        {/* Conclusion */}
        <div>
          <h4 className="text-lg font-semibold mb-3">
            What Set This Project Apart?
          </h4>
          <p className="text-gray-300 font-light font-kanit">
            {project.conclusion}
          </p>
        </div>
      </div>
    </div>
  );
});

// Main component with optimizations
const SuccessStories = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const modalRef = useRef(null);
  const [viewportSettings, setViewportSettings] = useState({
    numberOfCircles: 3,
    size: 300
  });

  // Optimize intersection observer with useCallback
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, disconnect the observer to save resources
          observer.disconnect();
        }
      },
      { threshold: 0.3, rootMargin: "0px" }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Close modal when clicking outside - optimized with useCallback
  useEffect(() => {
    if (!isModalOpen) return;
    
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen]);

  // Handle project selection and modal open - memoized with useCallback
  const openProjectModal = React.useCallback((project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden"; // Prevent scrolling when modal is open
  }, []);

  // Close modal - memoized with useCallback
  const closeModal = React.useCallback(() => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto"; // Re-enable scrolling
  }, []);

  // Handle viewport settings with debounce
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        setViewportSettings({
          numberOfCircles: width < 640 ? 2 : width < 1024 ? 3 : 4,
          size: width < 640 ? 200 : width < 1024 ? 300 : 400
        });
      }, 250); // 250ms debounce
    };

    // Set initial values
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      {/* Header Section */}
      <div
        ref={sectionRef}
        className="border-b border-gray-600 text-center px-4 relative overflow-hidden">
        <GradientCircleAnimation
          numberOfCircles={viewportSettings.numberOfCircles}
          size={viewportSettings.size}
          className="absolute top-0 overflow-hidden"
        />
        <div className="flex flex-col gap-4 my-12 mt-36 relative z-10">
          <div
            className={`max-w-6xl mx-auto bg-white/70 text-transparent bg-clip-text mb-4 ${
              isVisible
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-10"
            } transition-all ease-in duration-200`}>
            <h1 className="font-bold text-5xl md:text-7xl">Success Stories</h1>
            <h2 className="text-4xl md:text-6xl font-medium">
              Digital Excellence in Action
            </h2>
          </div>
          <p
            className={`text-lg font-kanit max-w-5xl mx-auto bg-white/70 text-transparent bg-clip-text mb-8 ${
              isVisible
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            } transition-all ease-in duration-200 delay-100`}>
            At Cypera Digital, we don't just build solutions—we craft digital experiences that transform businesses. 
            Explore how we've helped our clients overcome challenges, achieve their goals, and redefine excellence 
            in their industries.
          </p>
        </div>

        <div className="flex justify-center items-center h-fit">
          <div
            className="relative w-[70%] md:w-[60%] lg:w-[40%] aspect-video shadow-2xl shadow-blue-400 ring-8 rounded-lg"
            style={{
              transform:
                "perspective(542px) rotate(22deg) rotateX(8.5deg) rotateY(-28deg) skewX(0.6deg) skewY(-16.3deg)",
              transformOrigin: "100% 50% 0px",
              translate: "-3em",
            }}>
            <iframe
              src="https://cyperadigital.com/"
              title="Cypera Digital"
              className="w-full h-full rounded-lg shadow-lg border border-gray-300"
              loading="lazy"
              fetchpriority="auto"
              referrerpolicy="no-referrer"
              sandbox="allow-same-origin allow-scripts allow-downloads allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"></iframe>
          </div>
        </div>
      </div>

      {/* Projects Grid Section - Using virtualized rendering for better performance */}
      <div className="text-white py-12 px-5 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projectData.map((project) => (
            <ProjectCard 
              key={project.id} 
              project={project} 
              openProjectModal={openProjectModal} 
            />
          ))}
        </div>
      </div>

      {/* Modal - Only render when open for better performance */}
      {isModalOpen && selectedProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <ProjectModal 
            project={selectedProject} 
            modalRef={modalRef} 
            closeModal={closeModal} 
          />
        </div>
      )}
    </>
  );
};

export default React.memo(SuccessStories); // Memoize the entire component